import { PORTAL_URL } from "@/config/intake";
import { MembershipCTA } from "./MembershipCTA";
import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import logomark from "@/assets/logomark.png";
import { useActiveSection } from "@/hooks/use-active-section";

const NAV = [
  { label: "Treatments", href: "#treatments", id: "treatments" },
  { label: "Membership", href: "#membership", id: "membership" },
  { label: "How it works", href: "#how", id: "how" },
  { label: "Pricing", href: "#pricing", id: "pricing" },
  { label: "FAQ", href: "#faq", id: "faq" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection(NAV.map((n) => n.id));
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Mobile menu: Esc to close, focus-trap, body scroll-lock, focus first link on open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const first = menuRef.current?.querySelector<HTMLElement>("a, button");
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (e.key !== "Tab" || !menuRef.current) return;
      const focusables = menuRef.current.querySelectorAll<HTMLElement>(
        'a, button, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables.length) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-background/80 backdrop-blur-md border-b border-border/60" : "bg-transparent"
      }`}
    >
      <div className="container-wide flex items-center justify-between gap-4 h-16 md:h-20 lg:h-24">
        <a href="#" className="flex items-center gap-2 lg:gap-2.5 group shrink-0" aria-label="Olive Health home">
          <img
            src={logomark}
            alt=""
            width={56}
            height={56}
            className="size-9 md:size-11 lg:size-12"
          />
          <span className="font-display tracking-tight text-foreground text-2xl md:text-3xl lg:text-[2rem] leading-none whitespace-nowrap">
            Olive Health
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 min-w-0">
          {NAV.map((n) => {
            const isActive = active === n.id;
            return (
              <a
                key={n.href}
                href={n.href}
                aria-current={isActive ? "location" : undefined}
                className={`relative text-sm whitespace-nowrap transition-colors ${
                  isActive ? "text-foreground" : "text-foreground/70 hover:text-foreground"
                }`}
              >
                {n.label}
                <span
                  aria-hidden
                  className={`absolute -bottom-1 left-0 h-px bg-accent transition-[width] duration-500 ${
                    isActive ? "w-full" : "w-0"
                  }`}
                />
              </a>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3 xl:gap-4 shrink-0">
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm whitespace-nowrap text-foreground/70 hover:text-foreground transition-colors"
          >
            Sign in
          </a>
          <MembershipCTA
            placement="header_desktop"
            waitlistLabel="Join waitlist"
            className="rounded-full bg-primary text-primary-foreground text-sm whitespace-nowrap px-4 xl:px-5 py-2.5 hover:opacity-90 transition-opacity"
          >
            Become a member
          </MembershipCTA>
        </div>

        <button
          ref={toggleRef}
          className="lg:hidden p-2 -mr-2 text-foreground shrink-0"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {open && (
        <div
          id="mobile-nav"
          ref={menuRef}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          className="lg:hidden border-t border-border/60 bg-background/95 backdrop-blur-md"
        >
          <div className="container-wide py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="text-foreground/80">
                {n.label}
              </a>
            ))}
            <MembershipCTA
              placement="header_mobile"
              waitlistLabel="Join waitlist"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full bg-primary text-primary-foreground text-center px-5 py-3"
            >
              Start your visit
            </MembershipCTA>
          </div>
        </div>
      )}
    </header>
  );
};
